import { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPaw } from '@fortawesome/pro-regular-svg-icons';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import moment, { Moment } from 'moment-timezone';
import { RootState } from 'store';
import Staff from 'model/Staff';
import Spinner from 'components/UI/V3/Spinner';
import { WalkingSession } from '@spike/new-booking-model';
import { RoutedBookingAlerts } from '../bookingAlerts';
import BookingAlertList from '../BookingAlertList';
import SessionFullAlert from '../SessionFullAlert';
import styles from './SessionSelector.module.scss';

// One selected date while several are selected; each lists its own sessions.
export interface SessionSelectorGroup {
    key: string;
    date: Moment;
    sessions: Array<WalkingSession>;
    loading?: boolean;
    selectedSessionIds: Array<number>;
    // The alerts checked for each selected session, keyed by session id, shown under its card.
    alertsBySessionId?: Record<number, RoutedBookingAlerts>;
}

interface Props {
    sessions: Array<WalkingSession>;
    loading?: boolean;
    date?: Moment;
    selectedSessionId?: number;
    onSelect: (session: WalkingSession) => void;
    // When set, the sessions are listed per date and `sessions`, `date` and `selectedSessionId`
    // are ignored.
    groups?: Array<SessionSelectorGroup>;
    onSelectInGroup?: (session: WalkingSession, groupKey: string) => void;
    // Named by the session full alert under a card.
    serviceName?: string;
}

const CARD_SCROLL = 178;
const SCROLL_EPSILON = 2;

const formatTime = (startTime: string): string => moment(startTime, 'HH:mm').format('h:mma');

interface TrackProps {
    heading: ReactNode;
    sessions: Array<WalkingSession>;
    loading?: boolean;
    date?: Moment;
    isSelected: (session: WalkingSession) => boolean;
    onSelect: (session: WalkingSession) => void;
    // When set, each card sits in a slot with room for its alerts below it.
    renderBelowCard?: (session: WalkingSession) => ReactNode;
}

const sessionAlertsBelow = (alerts: RoutedBookingAlerts | undefined, serviceName?: string): ReactNode => {
    if (!alerts) return null;

    const others = [...alerts.session, ...alerts.staff, ...alerts.dateTime, ...alerts.zone, ...alerts.general];
    if (alerts.sessionFull.length === 0 && others.length === 0) return null;

    return (
        <div className={styles.cardAlerts}>
            {alerts.sessionFull.length > 0 && (
                <SessionFullAlert
                    compact
                    serviceName={serviceName}
                />
            )}
            <BookingAlertList
                compact
                alerts={others}
            />
        </div>
    );
};

const SessionTrack: FunctionComponent<TrackProps> = ({
    heading,
    sessions,
    loading,
    date,
    isSelected,
    onSelect,
    renderBelowCard
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const syncArrows = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;

        setCanScrollLeft(track.scrollLeft > SCROLL_EPSILON);
        setCanScrollRight(track.scrollLeft + track.clientWidth < track.scrollWidth - SCROLL_EPSILON);
    }, []);

    useEffect(() => {
        syncArrows();
    }, [sessions, syncArrows]);

    const scrollBy = (direction: 1 | -1) => {
        trackRef.current?.scrollBy({ left: direction * CARD_SCROLL, behavior: 'smooth' });
    };

    const avatarOf = (staffId: number): string => staffs.find(staff => staff.id === staffId)?.person.avatar || '';

    const dateLabel = date?.isValid() ? date.format('D MMM') : '';

    return (
        <div>
            <div className={styles.header}>
                {heading}
                {sessions.length > 0 && (
                    <div className={styles.arrows}>
                        <button
                            type="button"
                            aria-label="Previous sessions"
                            className={styles.arrow}
                            disabled={!canScrollLeft}
                            onClick={() => scrollBy(-1)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                            type="button"
                            aria-label="Next sessions"
                            className={styles.arrow}
                            disabled={!canScrollRight}
                            onClick={() => scrollBy(1)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <Spinner />
            ) : sessions.length === 0 ? (
                <p className={styles.emptyState}>
                    No sessions are configured for this date. You can still book any time you need.
                </p>
            ) : (
                <div
                    ref={trackRef}
                    className={styles.track}
                    onScroll={syncArrows}
                >
                    {sessions.map(session => {
                        const { capacity, bookedPets } = session;
                        const knownOccupancy = bookedPets !== undefined && capacity !== undefined && capacity > 0;
                        const isFull = knownOccupancy && bookedPets! >= capacity!;
                        const selected = isSelected(session);

                        const card = (
                            <button
                                type="button"
                                key={session.id}
                                aria-pressed={selected}
                                className={clsx(styles.card, {
                                    [styles.cardSelected]: selected
                                })}
                                onClick={() => onSelect(session)}
                            >
                                <span className={styles.cardTop}>
                                    <span>{dateLabel}</span>
                                    <span>{formatTime(session.startTime)}</span>
                                </span>

                                <span className={styles.bar}>
                                    <span
                                        className={clsx(styles.barFill, { [styles.barFillFull]: isFull })}
                                        style={{
                                            width: knownOccupancy
                                                ? `${Math.min(100, (bookedPets! / capacity!) * 100)}%`
                                                : 0
                                        }}
                                    />
                                </span>

                                <span className={clsx(styles.occupancy, { [styles.occupancyFull]: isFull })}>
                                    <span>{isFull ? 'Full' : 'Booked'}</span>
                                    <span className={styles.occupancyBadge}>
                                        <FontAwesomeIcon icon={faPaw} />
                                        {knownOccupancy ? `${bookedPets}/${capacity}` : `—/${capacity ?? '—'}`}
                                    </span>
                                </span>

                                <span className={styles.staffRow}>
                                    <Avatar
                                        alt={session.staffName}
                                        src={avatarOf(session.staffId)}
                                        className={styles.staffAvatar}
                                    />
                                    <span className={styles.staffName}>{session.staffName}</span>
                                </span>
                            </button>
                        );

                        return renderBelowCard ? (
                            <div
                                key={session.id}
                                className={styles.cardSlot}
                            >
                                {card}
                                {renderBelowCard(session)}
                            </div>
                        ) : (
                            card
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const SessionSelector: FunctionComponent<Props> = ({
    sessions,
    loading,
    date,
    selectedSessionId,
    onSelect,
    groups,
    onSelectInGroup,
    serviceName
}) => {
    const title = <span className={styles.title}>Available Sessions</span>;

    if (!groups) {
        return (
            <SessionTrack
                heading={title}
                sessions={sessions}
                loading={loading}
                date={date}
                isSelected={session => session.id === selectedSessionId}
                onSelect={onSelect}
            />
        );
    }

    return (
        <div>
            <div className={styles.header}>{title}</div>
            <div className={styles.groups}>
                {groups.map(group => (
                    <SessionTrack
                        key={group.key}
                        heading={<span className={styles.groupTitle}>{group.date.format('ddd, D MMM')}</span>}
                        sessions={group.sessions}
                        loading={group.loading}
                        date={group.date}
                        isSelected={session => group.selectedSessionIds.includes(session.id)}
                        onSelect={session => onSelectInGroup?.(session, group.key)}
                        renderBelowCard={session =>
                            sessionAlertsBelow(group.alertsBySessionId?.[session.id], serviceName)
                        }
                    />
                ))}
            </div>
        </div>
    );
};

export default SessionSelector;
